/**
 * The footer every market surface carries: where the numbers came from, how
 * fresh they are, and the line that keeps this on the right side of the
 * valuation rules — «مؤشر إحصائي استرشادي — ليس تقييماً».
 *
 * It is not decoration and it is not optional. A number without its source and
 * its period is the thing that loses a broker's trust the first time it is
 * questioned.
 */

import { useTranslation } from 'react-i18next';
import { latnLocale } from '../../utils/numberFormat';

export interface MarketSourceNoteProps {
  /** Which sources actually contributed to what is shown above. */
  sale?: boolean;
  rent?: boolean;
  /** Snapshot date, ISO. */
  updatedAt?: string;
  className?: string;
}

export default function MarketSourceNote({
  sale = true,
  rent = false,
  updatedAt,
  className = '',
}: MarketSourceNoteProps) {
  const { t, i18n } = useTranslation('marketData');

  const sources = [
    sale ? t('source.sale', { defaultValue: 'Open sale-deed data - Ministry of Justice' }) : null,
    rent ? t('source.rent', { defaultValue: 'Rent indicators - Real Estate General Authority' }) : null,
  ].filter(Boolean) as string[];

  const updated = updatedAt
    ? new Intl.DateTimeFormat(latnLocale(i18n.language === 'ar' ? 'ar-SA' : 'en-GB'), {
        year: 'numeric',
        month: 'long',
      }).format(new Date(updatedAt))
    : null;

  return (
    <div
      className={`border-t border-[var(--pv-border-subtle)] pt-3 text-[11px] leading-relaxed text-[var(--pv-color-text-subtle)] ${className}`}
    >
      <p className="font-medium text-[var(--pv-color-text-muted)]">
        {t('disclaimer', { defaultValue: 'A statistical guide - not a property valuation' })}
      </p>
      {sources.length > 0 && (
        <p className="mt-1">
          {t('source.label', { defaultValue: 'Source' })}: {sources.join(' · ')}
          {updated ? ` · ${t('source.updated', { date: updated, defaultValue: `Updated ${updated}` })}` : ''}
        </p>
      )}
    </div>
  );
}
